import { useMutation } from '@apollo/client';
import { cloneDeep } from 'lodash';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { PersonInput } from 'sr-types/lib/person/v1/graphql';
import { Contact, ContactInput, Reference } from 'sr-types/lib/production/v1/graphql';
import { ContactInfoInput } from 'sr-types/lib/search/v1/graphql';
import { UserContext } from '../common/auth/UserContext';
import { EMPTY_PERSON } from '../common/components/person/person';
import { CONSTANTS_ID } from '../common/constants/constants';
import { FormProvider } from '../common/form/FormContext';
import FormModal from '../common/form/FormModal';
import Validator from '../common/form/Validator';
import I18n, { useI18n } from '../common/i18n/I18n';
import { V } from '../common/Layout';
import DataList from '../common/list/DataList';
import GuestEditor from '../person/GuestEditor';
import { PERSON_TYPE } from '../person/utils/personConstants';
import SearchPanel from '../supply/searchkit/facets/SearchPanel';
import { ContactWithKey, getKeyPeopleInput, getPeopleWithKeyFlag, normalizeForKeyCheck } from './helpers/peopleUtils';
import { addPersonToProduction, client } from './helpers/production';
import { useSaveKeyPeople } from './helpers/productionApi';
import KeyPeopleListView from './KeyPeopleListView';
import KeyPeopleTile from './KeyPeopleTile';
import SearchOrCreateResult from './SearchOrCreateResult';

const defaultValidationRules = {
    name: [Validator.RULES.isRequired]
};

interface KeyPeopleModalProps {
    open?: boolean;
    onClose: () => void;
    peopleData: Contact[];
    keyPeople: Contact[];
    id?: string;
    productionId?: string;
    productionRef?: Reference;
    setViewState?: (viewState: any) => void;
}

type LinkedPeopleTypeInput = {
    resourceType?: string;
    resourceSubType?: string;
};

type AddKeyPeoplePersonState = PersonInput & LinkedPeopleTypeInput;

const makeEmptyKeyPeoplePersonState = (): AddKeyPeoplePersonState => ({
    ...cloneDeep(EMPTY_PERSON),
    resourceType: undefined,
    resourceSubType: undefined
});

const toContactInput = (input: PersonInput, id: string): ContactInput => {
    const { contactInfo, name, professionalRoles, creativeCredits, experiences } = input;

    const associatedPersonInput: ContactInput = {
        contactInfo: (contactInfo as ContactInfoInput) || undefined,
        name,
        personReference: {
            id: id,
            label: `${name.firstName} ${name.lastName}`,
            type: 'Person'
        },
        role: professionalRoles?.[0] || undefined
    };

    if (creativeCredits?.[0]) {
        const cc = creativeCredits[0];
        const { showReference, showName, department, professionalRoles } = cc;
        Object.assign(associatedPersonInput, {
            productionReference: showReference || undefined,
            production: showName || undefined,
            productionDepartment: department || undefined,
            productionRole: professionalRoles?.[0] || undefined
        });
    }

    if (experiences?.[0]) {
        const exp = experiences[0];
        const { experienceName, department, organizationReference, title } = exp;
        Object.assign(associatedPersonInput, {
            organization: experienceName || undefined,
            organizationDepartment: department || undefined,
            organizationReference: organizationReference || undefined,
            title: title || undefined
        });
    }

    Object.keys(associatedPersonInput).forEach(
        (key) => associatedPersonInput[key] === undefined && delete associatedPersonInput[key]
    );

    return associatedPersonInput;
};

export default function KeyPeopleModal({
    open,
    onClose,
    peopleData,
    keyPeople,
    id,
    productionId,
    productionRef,
    setViewState
}: KeyPeopleModalProps) {
    const [isCreate, setIsCreate] = useState(false);
    const [searchAllPeople, setSearchAllPeople] = useState(false);
    const [isPersonSaving, setIsPersonSaving] = useState({ isSaving: false, person: undefined });
    const { saveKeyPeople } = useSaveKeyPeople();
    const [isFormSaving, setIsFormSaving] = useState(false);
    const [query, setQuery] = useState('');
    const [isSaveDisabled, setIsSaveDisabled] = useState(false);
    const [savePersonToProduction] = useMutation(addPersonToProduction, { client });
    const guestEditorRef = useRef(null);
    const { activeOrganizationAccount } = useContext(UserContext);
    const [personState, setPersonState] = useState<AddKeyPeoplePersonState>(makeEmptyKeyPeoplePersonState);

    const searchPlaceholder = useI18n('production.header.form.searchKeyPeople');

    const peopleWithKey: ContactWithKey[] = useMemo(() => {
        return getPeopleWithKeyFlag(peopleData, keyPeople);
    }, [peopleData, keyPeople]);

    const filteredPeopleList: ContactWithKey[] = useMemo(() => {
        const people = getPeopleWithKeyFlag(peopleData, keyPeople);

        if (query) {
            const searchWords = query.toLowerCase().split(' ');
            return people.filter((person) =>
                searchWords.every(
                    (word) =>
                        person.contactInfo?.email?.address.toLowerCase().includes(word) ||
                        person.name.firstName.toLowerCase().includes(word) ||
                        person.name.lastName.toLowerCase().includes(word) ||
                        person.role?.toLowerCase().includes(word)
                )
            );
        }

        return people;
    }, [peopleData, keyPeople, query]);

    useEffect(() => {
        setSearchAllPeople(false);
    }, [query]);

    useEffect(() => {
        if (Array.isArray(filteredPeopleList) && filteredPeopleList.length === 0) {
            setSearchAllPeople(true);
        }
    }, [filteredPeopleList]);

    const handleAfterRefetch = () => {
        setQuery('');
        setIsCreate(false);
        setSearchAllPeople(false);
        setIsFormSaving(false);
        setIsPersonSaving({ isSaving: false, person: undefined });
        setPersonState(makeEmptyKeyPeoplePersonState());
    };

    return (
        <FormProvider validationRules={defaultValidationRules} state={personState} setState={setPersonState}>
            <FormModal
                width={'580px'}
                isOpen={open}
                onClose={() => {
                    setIsCreate(false);
                    setSearchAllPeople(false);
                    onClose();
                }}
                isSaving={isFormSaving}
                isSaveDisabled={isSaveDisabled}
                title={<I18n token="form.people.addKeyPeople" />}
                id={`keyDates`}
                loadingButtonVariant="contained"
                hideButtons={!isCreate}
                onSave={() => {
                    setIsFormSaving(true);
                    guestEditorRef.current.handleSave(
                        (input, id) => {
                            const associatedPersonInput = toContactInput(input, id);

                            const variables = {
                                productionId: productionId,
                                input: {
                                    new: {
                                        contact: associatedPersonInput,
                                        isHired: false,
                                        resourceType: personState.resourceType,
                                        resourceSubType: personState.resourceSubType
                                    }
                                }
                            };
                            savePersonToProduction({
                                variables: variables,
                                context: {
                                    headers: {
                                        ownerId: activeOrganizationAccount
                                    }
                                }
                            })
                                .then(() => {
                                    const normalizedForKeyCheck = normalizeForKeyCheck(variables.input.new?.contact);
                                    saveKeyPeople(
                                        productionId,
                                        getKeyPeopleInput(peopleWithKey, undefined, undefined, normalizedForKeyCheck),
                                        true
                                    )
                                        .then(() => {
                                            setTimeout(() => {
                                                client.refetchQueries({ include: ['Production'] }).finally(() => {
                                                    handleAfterRefetch();
                                                });
                                            }, 300);
                                        })
                                        .catch(() => {
                                            setIsFormSaving(false);
                                        });
                                })
                                .catch(() => {
                                    setIsFormSaving(false);
                                });
                        },
                        () => {
                            setIsFormSaving(false);
                        }
                    );
                }}
                additionalActions={[
                    {
                        label: useI18n('dialog.cancel'),
                        onClick: () => {
                            setIsCreate(false);
                            setSearchAllPeople(false);
                            setQuery('');
                        },
                        variant: 'text'
                    }
                ]}
            >
                {isCreate ? (
                    <GuestEditor
                        isInModal
                        productionRef={productionRef}
                        entityIdFromModal={CONSTANTS_ID.createId}
                        setIsSaveDisabled={setIsSaveDisabled}
                        showTypeFields
                        ref={guestEditorRef}
                    />
                ) : (
                    <>
                        <SearchPanel
                            id={'people-serach'}
                            query={query}
                            setQuery={setQuery}
                            loading={false}
                            sx={{ width: '100%', p: 1 }}
                            searchPlaceholder={searchPlaceholder}
                            mobile={false}
                        />
                        {searchAllPeople && query ? (
                            <KeyPeopleListView
                                query={query}
                                productionId={productionId}
                                peopleWithKey={peopleWithKey}
                                setSearchAllPeople={setSearchAllPeople}
                                setIsCreate={setIsCreate}
                                setQuery={setQuery}
                            />
                        ) : (
                            <V
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    pb: 2,
                                    width: '100%',
                                    height: '100%',
                                    overflow: 'auto'
                                }}
                            >
                                <DataList
                                    items={filteredPeopleList}
                                    onClick={undefined}
                                    disableRipple
                                    ItemRenderer={(props) => {
                                        return (
                                            <KeyPeopleTile
                                                {...props}
                                                isLoading={isPersonSaving}
                                                onMarkClick={(person) => {
                                                    setIsPersonSaving({
                                                        person,
                                                        isSaving: true
                                                    });
                                                    setViewState((prev) => ({
                                                        ...prev,
                                                        keyPeople: getKeyPeopleInput(peopleWithKey, person, undefined)
                                                    }));
                                                    saveKeyPeople(
                                                        productionId,
                                                        getKeyPeopleInput(peopleWithKey, person, undefined),
                                                        true
                                                    )
                                                        .catch(() => {
                                                            setViewState((prev) => ({
                                                                ...prev,
                                                                keyPeople
                                                            }));
                                                        })
                                                        .finally(() => handleAfterRefetch());
                                                }}
                                                onUnmarkClick={(person) => {
                                                    setIsPersonSaving({
                                                        person,
                                                        isSaving: true
                                                    });
                                                    setViewState((prev) => ({
                                                        ...prev,
                                                        keyPeople: getKeyPeopleInput(peopleWithKey, undefined, person)
                                                    }));
                                                    saveKeyPeople(
                                                        productionId,
                                                        getKeyPeopleInput(peopleWithKey, undefined, person),
                                                        true
                                                    )
                                                        .catch(() => {
                                                            setViewState((prev) => ({
                                                                ...prev,
                                                                keyPeople: keyPeople
                                                            }));
                                                        })
                                                        .finally(() => handleAfterRefetch());
                                                }}
                                            />
                                        );
                                    }}
                                />
                            </V>
                        )}
                    </>
                )}
                {query && !isCreate && (
                    <SearchOrCreateResult
                        query={query}
                        setAllSearch={setSearchAllPeople}
                        setIsCreate={setIsCreate}
                        setState={setPersonState}
                        state={personState}
                        type={PERSON_TYPE}
                        isSearchedInDirectory={searchAllPeople}
                    />
                )}
            </FormModal>
        </FormProvider>
    );
}
